'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { SmartImage } from './smartimage';
import './table.css';
const debug = false;
/* eslint-disable-next-line */
function isImageURL(url) {
    const isImage = /\.(jpeg|jpg|gif|png|webp|svg|bmp)$/i.test(url);
    const isURL = () => { try {
        new URL(url);
        return true;
    }
    catch {
        return false;
    } };
    return isImage && isURL;
}
/**
 * Table — renders a simple data table with optional client-side column sorting.
 *
 * @param {array} [props.data] - Array of row objects (each object's keys become column headers).
 * @param {string} [props.id] - DOM id for the table element (required for sortable headers to find the table).
 * @param {boolean} [props.sortable] - Enable click-to-sort on column headers when true.
 * @param {string} [props.altRowColor] - Optional alternate-row background color for zebra striping.
 */
Table.propTypes = {
    /** Array of row objects; object keys become table columns. */
    data: PropTypes.array.isRequired,
    /** DOM id used for sorting and header lookups. */
    id: PropTypes.string.isRequired,
    /** Enable clickable column headers for client-side sorting. */
    sortable: PropTypes.bool,
    /** CSS color value used for alternate rows when zebra striping is desired. */
    altRowColor: PropTypes.string,
};
export function Table(props) {
    const [tableData, setTableData] = useState(props.data);
    const hasRows = Array.isArray(tableData) && tableData.length > 0;
    function getNestedTableId(parentKey, rowIndex, cellIndex) {
        return `${props.id}-${parentKey}-${rowIndex}-${cellIndex}`;
    }
    function getHeadings(data) {
        if (!data || data.length === 0 || !data[0]) {
            return null;
        }
        const headings = Object.keys(data[0]).map((key, i) => {
            return (props.sortable && props.sortable == true)
                ? _jsxs("th", { onClick: () => { sortTable(key); }, children: [_jsx("span", { children: key }), " ", _jsx("span", { className: "sort-arrow" })] }, i)
                : _jsx("th", { children: _jsx("span", { children: key }) }, i);
        });
        return _jsx("tr", { children: headings });
    }
    // eslint-disable-next-line
    function getRows(data) {
        if (!data || data.length === 0) {
            return _jsx("tr", { children: _jsx("td", { colSpan: 1, children: "No data available." }) });
        }
        return data.map((obj, i) => {
            const rowStyle = (props.altRowColor && i % 2 === 1) ? { backgroundColor: props.altRowColor } : {};
            return _jsx("tr", { style: rowStyle, children: getCells(obj) }, i);
        });
    }
    function getCells(obj) {
        // Use entries so we have access to the column key when rendering nested tables
        return Object.entries(obj).map(([key, value], i) => {
            // Defensive rendering: handle React nodes, images, arrays, and objects gracefully
            const myValue = (() => {
                // If it's already a React element, render it directly
                if (React.isValidElement(value))
                    return value;
                if (isImageURL(value))
                    return _jsx(SmartImage, { src: value, title: String(value), alt: String(value) });
                if (value === null || value === undefined)
                    return '';
                if (Array.isArray(value)) {
                    if (value.length > 0 && value.every((entry) => entry && typeof entry === 'object' && !React.isValidElement(entry))) {
                        return _jsx(Table, { id: getNestedTableId(key, i, 0), data: value });
                    }
                    return value.join(', ');
                }
                if (typeof value === 'object') {
                    const nestedRows = Object.entries(value).map(([nestedKey, nestedValue]) => ({
                        field: nestedKey,
                        value: nestedValue,
                    }));
                    return _jsx(Table, { id: getNestedTableId(key, i, 0), data: nestedRows });
                }
                return value;
            })();
            return _jsx("td", { "data-testid": `cell-${i}`, children: myValue }, i);
        });
    }
    /* ========== SORT FUNCTIONS ========== */
    function getHeader(column) {
        const table = document.getElementById(props.id);
        const headers = table.querySelectorAll('th');
        let myHeader = undefined;
        headers.forEach(header => {
            if (header.innerText.trim().toUpperCase() === column.trim().toUpperCase()) {
                myHeader = header;
            }
        });
        return (myHeader);
    }
    function getDirection(header) {
        const arrow = header.querySelector('.sort-arrow');
        let oldDirection = '';
        if (arrow) {
            const oldClassList = arrow.classList;
            if (oldClassList.contains('asc')) {
                oldDirection = 'asc';
            }
            else if (oldClassList.contains('desc')) {
                oldDirection = 'desc';
            }
        }
        return (oldDirection);
    }
    function clearAllArrows() {
        const table = document.getElementById(props.id);
        const headers = table.querySelectorAll('th');
        headers.forEach(header => {
            const arrow = header.querySelector('.sort-arrow');
            if (arrow) {
                arrow.classList.remove('asc', 'desc');
            }
        });
    }
    function updateArrow(column, oldDirection) {
        const header = getHeader(column);
        const arrow = header.querySelector('.sort-arrow');
        if (arrow) {
            if (oldDirection == 'asc') {
                arrow.classList.add('desc');
            }
            else {
                arrow.classList.add('asc');
            }
        }
    }
    function sortTable(column) {
        const oldTableData = [...tableData];
        const myHeader = getHeader(column);
        if (myHeader && getDirection(myHeader) === 'asc') {
            setTableData(oldTableData.sort((a, b) => b[column].localeCompare(a[column])));
        }
        else {
            setTableData(oldTableData.sort((a, b) => a[column].localeCompare(b[column])));
        }
        if (myHeader) {
            const oldDirection = getDirection(myHeader);
            clearAllArrows();
            updateArrow(column, oldDirection);
        }
    }
    return (_jsx("div", { children: _jsxs("table", { id: props.id ?? undefined, className: "pix-table", children: [_jsx("thead", { children: getHeadings(tableData) }), _jsx("tbody", { children: getRows(tableData) })] }) }));
}
;
